import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, Calendar, Mail, Star, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-body text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const statCards = [
    { icon: Package, label: 'Total Packages', value: stats?.total_packages || 0, color: 'bg-emerald-500' },
    { icon: Calendar, label: 'Total Bookings', value: stats?.total_bookings || 0, color: 'bg-green-500' },
    { icon: Mail, label: 'Contact Inquiries', value: stats?.total_contacts || 0, color: 'bg-purple-500' },
    { icon: Star, label: 'Testimonials', value: stats?.total_testimonials || 0, color: 'bg-yellow-500' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-3xl text-navy mb-6">Dashboard Overview</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon size={24} className="text-white" />
                </div>
                <TrendingUp size={20} className="text-green-500" />
              </div>
              <p className="font-body text-sm text-muted-foreground mb-1">{stat.label}</p>
              <p className="font-heading text-3xl font-semibold text-navy">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="font-heading text-xl text-navy mb-4">Recent Bookings</h2>
        {stats?.recent_bookings && stats.recent_bookings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left font-body text-sm font-semibold text-navy py-3">Name</th>
                  <th className="text-left font-body text-sm font-semibold text-navy py-3">Package</th>
                  <th className="text-left font-body text-sm font-semibold text-navy py-3">Guests</th>
                  <th className="text-left font-body text-sm font-semibold text-navy py-3">Status</th>
                  <th className="text-left font-body text-sm font-semibold text-navy py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_bookings.map((booking) => (
                  <tr key={booking.id} className="border-b border-border/50">
                    <td className="font-body text-sm py-3">{booking.name}</td>
                    <td className="font-body text-sm py-3">{booking.package_name}</td>
                    <td className="font-body text-sm py-3">{booking.guests}</td>
                    <td className="py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {booking.status}
                      </span>
                    </td>
                    <td className="font-body text-sm text-muted-foreground py-3">
                      {booking.preferred_date}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="font-body text-muted-foreground text-center py-8">No bookings yet</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
